import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import {
  useChargingKeyActivateChargingKey,
  useChargingKeyAssignChargingKey,
  useChargingKeyGetChargingKeys,
  useChargingKeyUnassignChargingKey,
} from 'api/charging-key/charging-key';
import { UserModel } from 'api/model';
import SectionHeader from 'components/SectionHeader';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import TextButton from 'components/inputs/TextButton';
import Roles from 'constants/Roles';
import useMe from 'contexts/authentication/useMe';
import { FC, useState } from 'react';
import styled, { css } from 'styled-components';
// Importera inte ActivateChargingKeyModal (eller något annat som drar in UserPicker) här:
// UserPicker → UserModalLink → UserFormModal → den här filen blir en cirkulär import som ger
// vit skärm ("Cannot access '__WEBPACK_DEFAULT_EXPORT__' before initialization").
import {
  chargingKeyLabel,
  toastChargingKeyResult,
} from './PaymentCards/chargingKeyUtils';

const Wrapper = styled.div`
  width: 50%;
  margin-top: 20px;
`;

const KeyRow = styled.div<{ isActiveKey?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${(props) =>
    !props.isActiveKey &&
    css`
      color: ${({ theme }) => theme.colors.border.primary};
      font-style: italic;
    `}
`;

const KeyList = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

interface Props {
  user: UserModel;
}

/** Fortum-laddnycklar för en fälttestare. Betalkorten (Circle K) ligger i UserPaymentCardForm. */
const UserChargingKeyForm: FC<Props> = ({ user }) => {
  const queryClient = useQueryClient();
  const me = useMe();
  const canEdit = me?.roles.includes(Roles.CircleK);
  const [selectedKeyId, setSelectedKeyId] = useState<number | null>(null);
  const [newKeyNumber, setNewKeyNumber] = useState('');

  const chargingKeys = useChargingKeyGetChargingKeys();
  const assignCall = useChargingKeyAssignChargingKey();
  const unassignCall = useChargingKeyUnassignChargingKey();
  const activateCall = useChargingKeyActivateChargingKey();
  const isBusy =
    assignCall.isLoading || unassignCall.isLoading || activateCall.isLoading;

  const keys = chargingKeys.data?.keys ?? [];
  const usersKeys = keys.filter((k) => k.cardHolderID === user.userID);
  // Även avstängda nycklar går att dela ut — de slås på hos Fortum vid tilldelningen. Fortum tar
  // månadsavgift för påslagna nycklar, så de hålls avstängda tills de faktiskt används. Utan
  // integrationen går de inte att slå på, och då visas bara påslagna.
  const integrationEnabled = chargingKeys.data?.integrationEnabled ?? false;
  const freeKeys = keys.filter(
    (k) => k.cardHolderID == null && (k.isActive || integrationEnabled)
  );

  const refresh = () => queryClient.invalidateQueries(chargingKeys.queryKey);

  const assign = async () => {
    if (selectedKeyId === null) return;
    const wasOff = freeKeys.some(
      (k) => k.paymentCardId === selectedKeyId && !k.isActive
    );
    try {
      const result = await assignCall.mutateAsync({
        data: { paymentCardId: selectedKeyId, cardHolderID: user.userID },
      });
      toastChargingKeyResult(
        result,
        `${chargingKeyLabel(result.key)} är tilldelad ${user.name}${
          wasOff && result.key.isActive ? ' och påslagen hos Fortum' : ''
        }`
      );
      setSelectedKeyId(null);
      await refresh();
    } catch {
      // Felet visas som toast av den globala felhanteringen
    }
  };

  const activate = async () => {
    const number = newKeyNumber.trim();
    if (!number) return;
    try {
      const result = await activateCall.mutateAsync({
        data: { uidOrVisualNumber: number, cardHolderID: user.userID },
      });
      toastChargingKeyResult(
        result,
        `${chargingKeyLabel(result.key)} är aktiverad och tilldelad ${user.name}`
      );
      setNewKeyNumber('');
      await refresh();
    } catch {
      // Felet visas som toast av den globala felhanteringen
    }
  };

  const unassign = async (paymentCardId: number) => {
    try {
      const result = await unassignCall.mutateAsync({
        data: { paymentCardId },
      });
      toastChargingKeyResult(
        result,
        `${chargingKeyLabel(result.key)} är borttagen från ${user.name}`
      );
      await refresh();
    } catch {
      // Felet visas som toast av den globala felhanteringen
    }
  };

  // Utan nycklar i systemet och utan behörighet finns inget att visa
  if (!canEdit && usersKeys.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <SectionHeader>Laddnycklar (Fortum)</SectionHeader>
      {chargingKeys.isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
      <KeyList>
        {usersKeys.length === 0 && !chargingKeys.isLoading && (
          <i>Ingen laddnyckel</i>
        )}
        {usersKeys.map((key) => (
          <KeyRow key={key.paymentCardId} isActiveKey={key.isActive}>
            <p title={`UID ${key.uid}`}>
              {chargingKeyLabel(key)}
              {!key.isActive && ' (avstängd)'}
            </p>
            {canEdit && (
              <TextButton
                disabled={isBusy}
                onClick={() => unassign(key.paymentCardId)}
              >
                Ta bort
              </TextButton>
            )}
          </KeyRow>
        ))}
      </KeyList>
      {canEdit && (
        <Actions>
          <Select
            value={selectedKeyId ?? ''}
            disabled={isBusy || freeKeys.length === 0}
            onChange={(e) =>
              setSelectedKeyId(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">
              {freeKeys.length === 0 ? 'Inga lediga nycklar' : 'Välj ledig nyckel'}
            </option>
            {freeKeys.map((key) => (
              <option key={key.paymentCardId} value={key.paymentCardId}>
                {chargingKeyLabel(key)}
                {!key.isActive && ' (avstängd, slås på vid tilldelning)'}
              </option>
            ))}
          </Select>
          <Button disabled={isBusy || selectedKeyId === null} onClick={assign}>
            Tilldela
          </Button>
        </Actions>
      )}
      {canEdit && integrationEnabled && (
        <Actions style={{ marginTop: '10px' }}>
          <Input
            placeholder="Ny nyckel, t.ex. ZAKRA0001"
            value={newKeyNumber}
            disabled={isBusy}
            onChange={(e) => setNewKeyNumber(e.target.value)}
          />
          <Button disabled={isBusy || !newKeyNumber.trim()} onClick={activate}>
            Aktivera
          </Button>
        </Actions>
      )}
    </Wrapper>
  );
};

export default UserChargingKeyForm;
